import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { startLoadingBranding, setBranding, failLoadingBranding } from '../slices/brandingSlice';

/**
 * Fetches `/api/config/get-main_config` (public, no auth) and hydrates
 * the branding slice. Called once at app initialization from the root
 * layout / providers. The endpoint also returns environment + description
 * fields, which we ignore here — another thunk can consume those later.
 *
 * On failure we keep the hardcoded initial state (Kobotto / Smart Matrix),
 * so the UI never flashes "SmartMatrix" regardless of whether the fetch
 * succeeds, fails, or races against other startup work.
 */
interface MainConfigResponse {
  environment?: string;
  description?: string;
  branding?: {
    companyName: string;
    productName: string;
    productShortName: string;
    tagline: string;
    supportEmail: string;
    copyrightHolder: string;
    assistantName: string;
    logoUrl: string;
    logoIconUrl?: string;
    landingTheme?: string;
    heroDescription?: string;
  };
}

export const fetchBranding = createAsyncThunk<void, void>(
  'branding/fetch',
  async (_, { dispatch }) => {
    dispatch(startLoadingBranding());
    try {
      const response = await apiClient.get<MainConfigResponse>('/api/config/get-main_config');
      const branding = response.data?.branding;
      if (!branding) {
        dispatch(failLoadingBranding('Response missing branding object'));
        return;
      }
      // Optional fields default to empty / 'dark' so the slice always has
      // concrete string values — components read them synchronously.
      dispatch(setBranding({
        companyName:      branding.companyName,
        productName:      branding.productName,
        productShortName: branding.productShortName,
        tagline:          branding.tagline,
        supportEmail:     branding.supportEmail,
        copyrightHolder:  branding.copyrightHolder,
        assistantName:    branding.assistantName,
        logoUrl:          branding.logoUrl ?? '',
        logoIconUrl:      branding.logoIconUrl ?? '',
        landingTheme:     branding.landingTheme ?? 'dark',
        heroDescription:  branding.heroDescription ?? '',
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      logger.warn('fetchBranding failed, keeping default branding', { error: message });
      dispatch(failLoadingBranding(message));
    }
  }
);
